import React, { useState } from "react";
import { Box, Button, Typography, TextField, Dialog, DialogContent, IconButton, Alert } from "@mui/material";
import { Close as CloseIcon, Warning as WarningIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { SiteListItem } from "../../types";

interface DeleteSiteModalProps {
  site: SiteListItem;
  onClose: () => void;
  onDelete: () => Promise<void>;
}

export const DeleteSiteModal: React.FC<DeleteSiteModalProps> = ({ site, onClose, onDelete }) => {
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const canDelete = confirmText === site.name;

  const handleDelete = async () => {
    if (!canDelete) return;

    setIsDeleting(true);
    try {
      await onDelete();
    } catch (error) {
      console.error("Error deleting site:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "white",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "error.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningIcon sx={{ color: "white", fontSize: 28 }} />
            </Box>
            <Typography variant="h5" color="text.primary" fontWeight={600}>
              ลบ Site
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "grey.600" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              bgcolor: "rgba(239, 68, 68, 0.1)",
              color: "error.light",
              "& .MuiAlert-icon": { color: "error.main" },
            }}
          >
            การลบ Site นี้จะลบ <strong>ทุกหน้า</strong> ใน Site นี้ด้วย และไม่สามารถกู้คืนได้!
          </Alert>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "grey.100",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${site.theme.primaryColor} 0%, ${site.theme.secondaryColor} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem",
                }}
              >
                🏥
              </Box>
              <Box>
                <Typography color="text.primary" fontWeight={600}>
                  {site.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  /{site.slug} • {site.pageCount || 0} pages
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography color="text.secondary" sx={{ mb: 2 }}>
            พิมพ์ชื่อ Site <strong style={{ color: "#1a1a1a" }}>"{site.name}"</strong> เพื่อยืนยันการลบ
          </Typography>

          <TextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={site.name}
            autoFocus
            error={confirmText.length > 0 && !canDelete}
            helperText={confirmText.length > 0 && !canDelete ? "ชื่อไม่ตรงกัน" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "grey.50",
                "& fieldset": { borderColor: "grey.300" },
                "&:hover fieldset": { borderColor: "grey.400" },
                "&.Mui-focused fieldset": { borderColor: canDelete ? "error.main" : "grey.400" },
                "&.Mui-error fieldset": { borderColor: "error.main" },
              },
              "& .MuiInputBase-input": { color: "text.primary" },
              "& .MuiFormHelperText-root": { color: "error.main" },
            }}
          />
        </DialogContent>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 4,
            pt: 3,
            borderTop: 1,
            borderColor: "grey.200",
          }}
        >
          <Button onClick={onClose} sx={{ color: "grey.600", px: 3 }}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={!canDelete || isDeleting}
            startIcon={<DeleteIcon />}
            sx={{ px: 3 }}
          >
            {isDeleting ? "กำลังลบ..." : "ลบ Site"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
